import { isPlainRecord } from "../rules/checks.ts";
import { MAX_ITEM_STACK, TOOL_KINDS, type Rules, type ToolKind } from "../rules/types.ts";
import { BAG_SLOTS } from "./bag.ts";

/**
 * Harvesting (ADR-142 decision 3): the table of what may be harvested and what it gives, and its checks; the rolls of a
 * harvest and of the regrow time, and the index a map's harvestable props are numbered by, are `harvest-roll.ts`. The table is world
 * content, `tools/dev-server/harvest.json`, checked here against the rules' items; the rolls come from the shared
 * seeded hash, so the page, the map server and a tool agree on every one. Pure.
 */

/**
 * The most groups a table holds.
 */
export const MAX_HARVEST_GROUPS = 16;

/**
 * The most ranges of tiles a group lists, and the most frames those ranges name in all.
 */
export const MAX_HARVEST_RANGES = 8;
export const MAX_HARVEST_FRAMES = 4096;

/**
 * The most items a group's bonus holds, and the most one item's weight may be.
 */
export const MAX_BONUS_ITEMS = 8;
export const MAX_BONUS_WEIGHT = 1000;

/**
 * The longest group id and name, in characters.
 */
export const MAX_HARVEST_ID_CHARS = 32;
export const MAX_HARVEST_NAME_CHARS = 48;

/**
 * The longest hold, in ticks: half a minute at the server's rate of 20 a second.
 */
export const MAX_HARVEST_HOLD_TICKS = 600;

/**
 * The longest regrow time and spread, in ticks: a day at the server's rate.
 */
export const MAX_HARVEST_REGROW_TICKS = 1_728_000;

const PERCENT = 100;
const MAX_U32 = 0xffffffff;
/**
 * A group's id: lower-case letters, digits and `-`, starting with a letter or digit.
 */
export const HARVEST_GROUP_ID = new RegExp(`^[a-z0-9][a-z0-9-]{0,${MAX_HARVEST_ID_CHARS - 1}}$`);
const TABLE_FIELDS = ["groups"] as const;
const GROUP_FIELDS = ["id", "name", "tool", "tileset", "tiles", "yield", "bonus", "holdTicks", "regrowTicks", "regrowSpreadTicks"] as const;
const YIELD_FIELDS = ["item", "min", "max"] as const;
const BONUS_FIELDS = ["chance", "items"] as const;
const BONUS_ITEM_FIELDS = ["item", "weight"] as const;

/**
 * The fibre or other material a harvest always gives: an item and the least and most units.
 */
export interface HarvestYield {
  readonly item: string;
  readonly min: number;
  readonly max: number;
}

/**
 * One item a bonus may give and its weight against the others.
 */
export interface BonusItem {
  readonly item: string;
  readonly weight: number;
}

/**
 * What a harvest may give besides its yield: the chance in whole percents that it gives one, and the weighted items
 * it is picked from (a worm of the Aethling's three, 90, 9 and 1 provisionally).
 */
export interface HarvestBonus {
  readonly chance: number;
  readonly items: readonly BonusItem[];
}

/**
 * A group of harvestable frames that share a tool, a yield and a regrow: its id, its name, the tool kind a harvester
 * must hold, the tileset and the ranges of tiles (first and last, both included) that are harvestable, the yield, the
 * bonus, the ticks the harvest is held for, and the ticks a harvested prop takes to grow back plus the most a hash adds.
 */
export interface HarvestGroup {
  readonly id: string;
  readonly name: string;
  readonly tool: ToolKind;
  readonly tileset: string;
  readonly tiles: readonly (readonly [number, number])[];
  readonly yield: HarvestYield;
  readonly bonus: HarvestBonus;
  readonly holdTicks: number;
  readonly regrowTicks: number;
  readonly regrowSpreadTicks: number;
}

/**
 * The harvest table: 1 to 16 groups.
 */
export interface HarvestTable {
  readonly groups: readonly HarvestGroup[];
}

/**
 * The verdict on one harvest table document.
 */
export type HarvestTableVerdict = { readonly ok: true; readonly table: HarvestTable } | { readonly ok: false; readonly errors: readonly string[] };

/**
 * Whether a value is a whole number within a range.
 *
 * @param value The value.
 * @param min Least allowed.
 * @param max Most allowed.
 * @returns True when it is.
 */
function wholeIn(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * Reports each field of a record that a shape does not hold or lacks.
 *
 * @param value The record.
 * @param fields The fields it holds, each required.
 * @param path Its path.
 * @param what What it is called, for `is not a ... field`.
 * @param fail Where a problem goes.
 * @returns Whether it held exactly those fields.
 */
function exactFields(value: Readonly<Record<string, unknown>>, fields: readonly string[], path: string, what: string, fail: (message: string) => void): boolean {
  let ok = true;
  for (const key of Object.keys(value)) {
    if (!fields.includes(key)) {
      fail(`${path}${key} is not a ${what} field`);
      ok = false;
    }
  }
  for (const field of fields) {
    if (!Object.hasOwn(value, field)) {
      fail(`${path}${field} is missing`);
      ok = false;
    }
  }
  return ok;
}

/**
 * Checks a group's yield: an item the rules hold, and a quantity from `min` to `max` that an empty bag takes.
 *
 * @param value The yield.
 * @param path Its path.
 * @param rules The rules whose items it names.
 * @param fail Where a problem goes.
 * @returns The yield, or null.
 */
function yieldOf(value: unknown, path: string, rules: Rules, fail: (message: string) => void): HarvestYield | null {
  if (!isPlainRecord(value)) {
    fail(`${path} must be an object`);
    return null;
  }
  let ok = exactFields(value, YIELD_FIELDS, `${path}.`, "yield", fail);
  const item = value["item"];
  const record = typeof item === "string" ? rules.items.get(item) : undefined;
  if (record === undefined) {
    fail(`${path}.item names ${String(item)}, which the rules' items table does not hold`);
    ok = false;
  }
  const min = value["min"];
  const max = value["max"];
  if (!wholeIn(min, 1, MAX_ITEM_STACK)) {
    fail(`${path}.min must be a whole number from 1 to ${MAX_ITEM_STACK}`);
    ok = false;
  }
  if (!wholeIn(max, 1, MAX_ITEM_STACK)) {
    fail(`${path}.max must be a whole number from 1 to ${MAX_ITEM_STACK}`);
    ok = false;
  }
  if (wholeIn(min, 1, MAX_ITEM_STACK) && wholeIn(max, 1, MAX_ITEM_STACK)) {
    if (min > max) {
      fail(`${path}.min ${min} is above ${path}.max ${max}`);
      ok = false;
    } else if (record !== undefined && max > BAG_SLOTS * record.stack) {
      fail(`${path}.max ${max} is above ${BAG_SLOTS * record.stack}, the most ${record.id} an empty bag holds (${BAG_SLOTS} slots of ${record.stack})`);
      ok = false;
    }
  }
  return ok ? { item: item as string, min: min as number, max: max as number } : null;
}

/**
 * Checks a group's bonus: a chance of 1 to 100 whole percents and 1 to 8 distinct items the rules hold, each with a
 * whole weight.
 *
 * @param value The bonus.
 * @param path Its path.
 * @param rules The rules whose items it names.
 * @param fail Where a problem goes.
 * @returns The bonus, or null.
 */
function bonusOf(value: unknown, path: string, rules: Rules, fail: (message: string) => void): HarvestBonus | null {
  if (!isPlainRecord(value)) {
    fail(`${path} must be an object`);
    return null;
  }
  let ok = exactFields(value, BONUS_FIELDS, `${path}.`, "bonus", fail);
  const chance = value["chance"];
  if (!wholeIn(chance, 1, PERCENT)) {
    fail(`${path}.chance must be a whole percent from 1 to ${PERCENT}`);
    ok = false;
  }
  const list = value["items"];
  const items: BonusItem[] = [];
  if (!Array.isArray(list) || list.length < 1 || list.length > MAX_BONUS_ITEMS) {
    fail(`${path}.items must hold 1 to ${MAX_BONUS_ITEMS} items${Array.isArray(list) ? `, not ${list.length}` : ""}`);
    return null;
  }
  const seen = new Map<string, number>();
  for (const [index, entry] of list.entries()) {
    const where = `${path}.items[${index}]`;
    if (!isPlainRecord(entry)) {
      fail(`${where} must be an object`);
      ok = false;
      continue;
    }
    let entryOk = exactFields(entry, BONUS_ITEM_FIELDS, `${where}.`, "bonus item", fail);
    const item = entry["item"];
    if (typeof item !== "string" || !rules.items.has(item)) {
      fail(`${where}.item names ${String(item)}, which the rules' items table does not hold`);
      entryOk = false;
    } else if (seen.has(item)) {
      fail(`${where}.item ${item} is already named by items[${seen.get(item) as number}]`);
      entryOk = false;
    } else {
      seen.set(item, index);
    }
    const weight = entry["weight"];
    if (!wholeIn(weight, 1, MAX_BONUS_WEIGHT)) {
      fail(`${where}.weight must be a whole number from 1 to ${MAX_BONUS_WEIGHT}`);
      entryOk = false;
    }
    if (entryOk) {
      items.push({ item: item as string, weight: weight as number });
    } else {
      ok = false;
    }
  }
  return ok ? { chance: chance as number, items } : null;
}

/**
 * Checks a group's ranges of tiles: 1 to 8 pairs `[first, last]` of whole tile numbers that run up, overlap none
 * another and name no more than `MAX_HARVEST_FRAMES` frames in all.
 *
 * @param value The `tiles` field.
 * @param path Its path.
 * @param fail Where a problem goes.
 * @returns The ranges, or null.
 */
function tilesOf(value: unknown, path: string, fail: (message: string) => void): [number, number][] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_HARVEST_RANGES) {
    fail(`${path} must hold 1 to ${MAX_HARVEST_RANGES} ranges${Array.isArray(value) ? `, not ${value.length}` : ""}`);
    return null;
  }
  const ranges: [number, number][] = [];
  let ok = true;
  let frames = 0;
  for (const [index, range] of value.entries()) {
    const where = `${path}[${index}]`;
    if (!Array.isArray(range) || range.length !== 2 || !wholeIn(range[0], 0, MAX_U32) || !wholeIn(range[1], 0, MAX_U32)) {
      fail(`${where} must be [first, last], whole tile numbers`);
      ok = false;
      continue;
    }
    const first = range[0] as number;
    const last = range[1] as number;
    if (first > last) {
      fail(`${where} runs from ${first} down to ${last}`);
      ok = false;
      continue;
    }
    const clash = ranges.findIndex(([from, to]) => first <= to && last >= from);
    if (clash >= 0) {
      fail(`${where} overlaps tiles[${clash}]`);
      ok = false;
      continue;
    }
    frames += last - first + 1;
    ranges.push([first, last]);
  }
  if (frames > MAX_HARVEST_FRAMES) {
    fail(`${path} name ${frames} frames, past the ${MAX_HARVEST_FRAMES} a group holds`);
    ok = false;
  }
  return ok ? ranges : null;
}

/**
 * Checks one group, reporting each problem at its place.
 *
 * @param value The group.
 * @param index Its place in the table.
 * @param rules The rules whose items it names.
 * @param fail Where a problem goes.
 * @returns The group, or null.
 */
function groupOf(value: unknown, index: number, rules: Rules, fail: (message: string) => void): HarvestGroup | null {
  const path = `groups[${index}]`;
  if (!isPlainRecord(value)) {
    fail(`${path} must be an object`);
    return null;
  }
  let ok = exactFields(value, GROUP_FIELDS, `${path}.`, "group", fail);
  const id = value["id"];
  if (typeof id !== "string" || !HARVEST_GROUP_ID.test(id)) {
    fail(`${path}.id must be 1 to ${MAX_HARVEST_ID_CHARS} lower-case letters, digits and -, starting with a letter or digit`);
    ok = false;
  }
  const name = value["name"];
  if (typeof name !== "string" || name.length < 1 || name.length > MAX_HARVEST_NAME_CHARS) {
    fail(`${path}.name must be text of 1 to ${MAX_HARVEST_NAME_CHARS} characters`);
    ok = false;
  }
  const tool = value["tool"];
  if (typeof tool !== "string" || !(TOOL_KINDS as readonly string[]).includes(tool)) {
    fail(`${path}.tool is ${String(tool)}, which is not a tool kind (${TOOL_KINDS.join(", ")})`);
    ok = false;
  }
  const tileset = value["tileset"];
  if (typeof tileset !== "string" || tileset.length < 1 || tileset.length > 128) {
    fail(`${path}.tileset must be a tileset id of 1 to 128 characters`);
    ok = false;
  }
  const tiles = tilesOf(value["tiles"], `${path}.tiles`, fail);
  const gives = yieldOf(value["yield"], `${path}.yield`, rules, fail);
  const bonus = bonusOf(value["bonus"], `${path}.bonus`, rules, fail);
  const hold = value["holdTicks"];
  if (!wholeIn(hold, 1, MAX_HARVEST_HOLD_TICKS)) {
    fail(`${path}.holdTicks must be a whole number of ticks from 1 to ${MAX_HARVEST_HOLD_TICKS}`);
    ok = false;
  }
  const regrow = value["regrowTicks"];
  if (!wholeIn(regrow, 1, MAX_HARVEST_REGROW_TICKS)) {
    fail(`${path}.regrowTicks must be a whole number of ticks from 1 to ${MAX_HARVEST_REGROW_TICKS}`);
    ok = false;
  }
  const spread = value["regrowSpreadTicks"];
  if (!wholeIn(spread, 0, MAX_HARVEST_REGROW_TICKS)) {
    fail(`${path}.regrowSpreadTicks must be a whole number of ticks from 0 to ${MAX_HARVEST_REGROW_TICKS}`);
    ok = false;
  }
  if (!ok || tiles === null || gives === null || bonus === null) {
    return null;
  }
  return { id: id as string, name: name as string, tool: tool as ToolKind, tileset: tileset as string, tiles, yield: gives, bonus, holdTicks: hold as number, regrowTicks: regrow as number, regrowSpreadTicks: spread as number };
}

/**
 * Checks a harvest table as its file holds it, each error naming the group and the field: 1 to 16 groups, each with a
 * distinct id, a tool kind of the closed set, a tileset and ranges of tiles no other group claims, a yield and a bonus
 * naming items the rules hold, and whole ticks for the hold, the regrow and its spread; and no field a table, a group
 * or a part of one does not hold.
 *
 * @param value The parsed file.
 * @param rules The rules whose items the groups name.
 * @returns The table, built from the checked fields, or every error found.
 * @example
 * validateHarvestTable(JSON.parse(text), loadRules());
 * // { ok: true, table } or { ok: false, errors: ["groups[0].yield.item names rope, ..."] }
 */
export function validateHarvestTable(value: unknown, rules: Rules): HarvestTableVerdict {
  if (!isPlainRecord(value)) {
    return { ok: false, errors: ["harvest: a harvest table must be a JSON object"] };
  }
  const errors: string[] = [];
  /**
   * Records one error against the table.
   *
   * @param message What is wrong with which field.
   */
  const fail = (message: string): void => {
    errors.push(`harvest: ${message}`);
  };
  const list = value["groups"];
  const groups: HarvestGroup[] = [];
  if (!Array.isArray(list) || list.length < 1 || list.length > MAX_HARVEST_GROUPS) {
    fail(`groups must hold 1 to ${MAX_HARVEST_GROUPS} groups${Array.isArray(list) ? `, not ${list.length}` : ""}`);
  } else {
    const ids = new Map<string, number>();
    const claimed: { readonly tileset: string; readonly first: number; readonly last: number; readonly group: string }[] = [];
    for (const [index, entry] of list.entries()) {
      const group = groupOf(entry, index, rules, fail);
      if (group === null) {
        continue;
      }
      if (ids.has(group.id)) {
        fail(`groups[${index}].id ${group.id} is held twice`);
        continue;
      }
      ids.set(group.id, index);
      let free = true;
      for (const [first, last] of group.tiles) {
        const taken = claimed.find((other) => other.tileset === group.tileset && first <= other.last && last >= other.first);
        if (taken !== undefined) {
          fail(`groups[${index}].tiles frame ${Math.max(first, taken.first)} of ${group.tileset} is already in group ${taken.group}`);
          free = false;
        }
      }
      if (free) {
        for (const [first, last] of group.tiles) {
          claimed.push({ tileset: group.tileset, first, last, group: group.id });
        }
        groups.push(group);
      }
    }
  }
  for (const key of Object.keys(value)) {
    if (!(TABLE_FIELDS as readonly string[]).includes(key)) {
      fail(`${key} is not a harvest table field`);
    }
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true, table: { groups } };
}

/**
 * A table as its file keeps it: its fields in the order a group is written, two spaces deep.
 *
 * @param table The table.
 * @returns The text, ending in a newline.
 */
export function serialiseHarvestTable(table: HarvestTable): string {
  const groups = table.groups.map((group) => ({
    id: group.id,
    name: group.name,
    tool: group.tool,
    tileset: group.tileset,
    tiles: group.tiles.map(([first, last]) => [first, last]),
    yield: { item: group.yield.item, min: group.yield.min, max: group.yield.max },
    bonus: { chance: group.bonus.chance, items: group.bonus.items.map((entry) => ({ item: entry.item, weight: entry.weight })) },
    holdTicks: group.holdTicks,
    regrowTicks: group.regrowTicks,
    regrowSpreadTicks: group.regrowSpreadTicks,
  }));
  return `${JSON.stringify({ groups }, null, 2)}\n`;
}

/**
 * A table's frames looked up by tileset and tile.
 */
export interface HarvestFrames {
  /**
   * The group a frame belongs to.
   *
   * @param tileset The frame's tileset id.
   * @param tile The frame's tile number in it.
   * @returns The group, or undefined for a frame no group names.
   */
  groupOf(tileset: string, tile: number): HarvestGroup | undefined;
}

/**
 * Indexes a table's frames, so a prop's frame is looked up in a few comparisons however many frames a group names.
 *
 * @param table A table `validateHarvestTable` admitted.
 * @returns The lookup.
 */
export function indexHarvestFrames(table: HarvestTable): HarvestFrames {
  const byTileset = new Map<string, { readonly first: number; readonly last: number; readonly group: HarvestGroup }[]>();
  for (const group of table.groups) {
    const ranges = byTileset.get(group.tileset) ?? [];
    for (const [first, last] of group.tiles) {
      ranges.push({ first, last, group });
    }
    byTileset.set(group.tileset, ranges);
  }
  return {
    groupOf: (tileset, tile) => byTileset.get(tileset)?.find((range) => tile >= range.first && tile <= range.last)?.group,
  };
}
